/*
 * color-mixer.js — engine for the /color-mixer tool.
 *
 * Five explicit mixing models (paint is the default and the whole point):
 *   paint      — Kubelka-Munk pigment mixing (spectral.js model, run through
 *                color-mixer-paint.js). Blue + yellow = green, like real paint.
 *   light      — additive: colours add like beams of light (linear-light SUM,
 *                clamped). Blue + yellow light = white.
 *   ink        — subtractive CMY: weighted multiply in linear light (filters).
 *   perceptual — OKLab weighted average — smooth blends / gradients.
 *   rgb        — naive sRGB (gamma-space) weighted average. Wrong on purpose:
 *                this is what most web "mixers" do, and why they give grey.
 *
 * PAINT WEIGHTS ARE VOLUMES (since 23.09.2026, link format v=2). spectral.js
 * squares its mix factor, so until then "3 : 1" on the sliders meant 9 : 1 of
 * paint — measured against real tint ladders, f² = volume is the reading that
 * matches paint (header of scripts/color-mixer/calibrate.cjs). A v1 link or
 * saved mix is converted on load (weight → weight²), so it shows the same
 * colour it always did.
 *
 * A row can be a real pigment ({pig: 'ultramarine_blue'}) instead of a hex:
 * then paint mode mixes its measured reflectance and strength instead of a
 * curve guessed from an sRGB triple.
 *
 * Colour maths (sRGB<->linear, HSL, CMYK, OKLab/OKLCh, WCAG contrast, CSS
 * parsing, nearest-name) is ported from the sibling color-converter tool.
 *
 * Browser-first (wires the DOM on load) but also loadable under Node for
 * unit-testing the pure maths: exports `CMX` via module.exports.
 */
(function (root) {
    'use strict';

    var SPECTRAL = root && root.spectral ? root.spectral
        : (typeof globalThis !== 'undefined' && globalThis.spectral ? globalThis.spectral : null);
    var PAINT = function () { return (root && root.CMXPaint) || (typeof globalThis !== 'undefined' && globalThis.CMXPaint) || null; };

    // ---------------------------------------------------------------- maths --
    var clamp01 = function (x) { return x < 0 ? 0 : x > 1 ? 1 : x; };
    var round = function (n, d) { d = d || 0; var p = Math.pow(10, d); return Math.round(n * p) / p; };

    // sRGB transfer function on the [0,1] range.
    function s2l(c) { return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }
    function l2s(c) { return c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055; }

    function rgbToHsl(r, g, b) {
        var max = Math.max(r, g, b), min = Math.min(r, g, b);
        var h = 0, s = 0, l = (max + min) / 2;
        if (max !== min) {
            var d = max - min;
            s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
            if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
            else if (max === g) h = (b - r) / d + 2;
            else h = (r - g) / d + 4;
            h *= 60;
        }
        return { h: h, s: s * 100, l: l * 100 };
    }
    function hslToRgb(h, s, l) {
        h = (((h % 360) + 360) % 360) / 360; s /= 100; l /= 100;
        if (s === 0) return { r: l, g: l, b: l };
        var q = l < 0.5 ? l * (1 + s) : l + s - l * s, p = 2 * l - q;
        var cv = function (t) {
            if (t < 0) t += 1; if (t > 1) t -= 1;
            if (t < 1 / 6) return p + (q - p) * 6 * t;
            if (t < 1 / 2) return q;
            if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
            return p;
        };
        return { r: cv(h + 1 / 3), g: cv(h), b: cv(h - 1 / 3) };
    }
    function rgbToCmyk(r, g, b) {
        var k = 1 - Math.max(r, g, b);
        if (k >= 1 - 1e-9) return { c: 0, m: 0, y: 0, k: 100 };
        return {
            c: ((1 - r - k) / (1 - k)) * 100,
            m: ((1 - g - k) / (1 - k)) * 100,
            y: ((1 - b - k) / (1 - k)) * 100,
            k: k * 100
        };
    }
    // sRGB(0..1) -> OKLab (Björn Ottosson)
    function rgbToOklab(r, g, b) {
        var R = s2l(r), G = s2l(g), B = s2l(b);
        var l = 0.4122214708 * R + 0.5363325363 * G + 0.0514459929 * B;
        var m = 0.2119034982 * R + 0.6806995451 * G + 0.1073969566 * B;
        var s = 0.0883024619 * R + 0.2817188376 * G + 0.6299787005 * B;
        var l_ = Math.cbrt(l), m_ = Math.cbrt(m), s_ = Math.cbrt(s);
        return {
            L: 0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_,
            a: 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_,
            b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_
        };
    }
    function oklabToRgb(L, A, B) {
        var l_ = L + 0.3963377774 * A + 0.2158037573 * B;
        var m_ = L - 0.1055613458 * A - 0.0638541728 * B;
        var s_ = L - 0.0894841775 * A - 1.2914855480 * B;
        var l = l_ * l_ * l_, m = m_ * m_ * m_, s = s_ * s_ * s_;
        var R = +4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
        var G = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
        var B2 = -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s;
        return { r: l2s(R), g: l2s(G), b: l2s(B2) };
    }
    function oklabToOklch(L, a, b) {
        var c = Math.sqrt(a * a + b * b);
        var h = Math.atan2(b, a) * 180 / Math.PI; if (h < 0) h += 360;
        return { L: L, c: c, h: h };
    }
    // OKLCh -> in-gamut sRGB by reducing chroma (binary search) — proper gamut
    // mapping, not a per-channel clamp that would skew hue.
    function inGamut(rgb) {
        return rgb.r >= -1e-4 && rgb.r <= 1 + 1e-4 &&
            rgb.g >= -1e-4 && rgb.g <= 1 + 1e-4 &&
            rgb.b >= -1e-4 && rgb.b <= 1 + 1e-4;
    }
    function gamutMapOklab(L, a, b) {
        var rgb = oklabToRgb(L, a, b);
        if (inGamut(rgb)) return { r: clamp01(rgb.r), g: clamp01(rgb.g), b: clamp01(rgb.b) };
        var lch = oklabToOklch(L, a, b);
        var lo = 0, hi = lch.c, best = { r: clamp01(rgb.r), g: clamp01(rgb.g), b: clamp01(rgb.b) };
        for (var i = 0; i < 24; i++) {
            var mid = (lo + hi) / 2;
            var hr = mid * Math.cos(lch.h * Math.PI / 180);
            var hb = mid * Math.sin(lch.h * Math.PI / 180);
            var test = oklabToRgb(L, hr, hb);
            if (inGamut(test)) { lo = mid; best = { r: clamp01(test.r), g: clamp01(test.g), b: clamp01(test.b) }; }
            else hi = mid;
        }
        return best;
    }

    // WCAG 2 contrast
    function relLum(c) { return 0.2126 * s2l(c.r) + 0.7152 * s2l(c.g) + 0.0722 * s2l(c.b); }
    function contrastRatio(c1, c2) {
        var l1 = relLum(c1), l2 = relLum(c2);
        var hi = Math.max(l1, l2), lo = Math.min(l1, l2);
        return (hi + 0.05) / (lo + 0.05);
    }

    // perceptual distance — OKLab Euclidean, scaled to a deltaE-like number.
    function deltaOK(c1, c2) {
        var a = rgbToOklab(c1.r, c1.g, c1.b), b = rgbToOklab(c2.r, c2.g, c2.b);
        var dL = a.L - b.L, da = a.a - b.a, db = a.b - b.b;
        return Math.sqrt(dL * dL + da * da + db * db) * 100;
    }

    // ---- colour-vision-deficiency simulation (Machado et al. severity 1.0,
    //      applied in linear light).
    var CVD = {
        protanopia: [0.152286, 1.052583, -0.204868, 0.114503, 0.786281, 0.099216, -0.003882, -0.048116, 1.051998],
        deuteranopia: [0.367322, 0.860646, -0.227968, 0.280085, 0.672501, 0.047413, -0.011820, 0.042940, 0.968881],
        tritanopia: [1.255528, -0.076749, -0.178779, -0.078411, 0.930809, 0.147602, 0.004733, 0.691367, 0.303900]
    };
    function simulateCVD(c, type) {
        var m = CVD[type]; if (!m) return { r: c.r, g: c.g, b: c.b };
        var R = s2l(c.r), G = s2l(c.g), B = s2l(c.b);
        var nr = m[0] * R + m[1] * G + m[2] * B;
        var ng = m[3] * R + m[4] * G + m[5] * B;
        var nb = m[6] * R + m[7] * G + m[8] * B;
        return { r: clamp01(l2s(clamp01(nr))), g: clamp01(l2s(clamp01(ng))), b: clamp01(l2s(clamp01(nb))) };
    }

    // ----------------------------------------------------------- formatting --
    function to255(c) { return [Math.round(clamp01(c.r) * 255), Math.round(clamp01(c.g) * 255), Math.round(clamp01(c.b) * 255)]; }
    function hex(c) {
        var v = to255(c);
        return '#' + v.map(function (x) { return x.toString(16).padStart(2, '0'); }).join('').toLowerCase();
    }
    function rgbStr(c) { var v = to255(c); return 'rgb(' + v[0] + ', ' + v[1] + ', ' + v[2] + ')'; }
    function hslStr(c) { var h = rgbToHsl(c.r, c.g, c.b); return 'hsl(' + round(h.h) + ', ' + round(h.s) + '%, ' + round(h.l) + '%)'; }
    function cmykStr(c) { var k = rgbToCmyk(c.r, c.g, c.b); return 'cmyk(' + round(k.c) + '%, ' + round(k.m) + '%, ' + round(k.y) + '%, ' + round(k.k) + '%)'; }
    function oklchStr(c) {
        var ok = rgbToOklab(c.r, c.g, c.b);
        var o = oklabToOklch(ok.L, ok.a, ok.b);
        return 'oklch(' + round(o.L * 100, 1) + '% ' + round(o.c, 3) + ' ' + round(o.h, 1) + ')';
    }

    // -------------------------------------------------------------- parsing --
    // Canvas-backed parse for any CSS colour incl. names (browser only).
    var probe = null;
    function ensureProbe() {
        if (probe === null && typeof document !== 'undefined') {
            try { probe = document.createElement('canvas').getContext('2d'); } catch (e) { probe = false; }
        }
        return probe;
    }
    function parseHex(s) {
        s = String(s).trim();
        var m6 = s.match(/^#?([0-9a-f]{6})$/i);
        if (m6) { var n = parseInt(m6[1], 16); return { r: ((n >> 16) & 255) / 255, g: ((n >> 8) & 255) / 255, b: (n & 255) / 255 }; }
        var m3 = s.match(/^#?([0-9a-f]{3})$/i);
        if (m3) { var x = m3[1]; var e = function (ch) { return parseInt(ch + ch, 16) / 255; }; return { r: e(x[0]), g: e(x[1]), b: e(x[2]) }; }
        return null;
    }
    function parse(s) {
        s = String(s == null ? '' : s).trim();
        if (!s) return null;
        var h = parseHex(s);
        if (h) return h;
        var p = ensureProbe();
        if (p) {
            // Two-sentinel validity check: an invalid color leaves fillStyle at
            // whatever it was, so a garbage string parsed against '#000' would
            // wrongly read back black. Parsing against BOTH '#000' and '#fff'
            // and requiring the results to agree rejects invalid input.
            p.fillStyle = '#000'; p.fillStyle = s; var c1 = p.fillStyle;
            p.fillStyle = '#fff'; p.fillStyle = s; var c2 = p.fillStyle;
            if (c1 !== c2) return null;
            var out = c1; // normalised to #rrggbb or rgba()
            var hh = parseHex(out);
            if (hh) return hh;
            var rgba = out.match(/rgba?\(([^)]+)\)/i);
            if (rgba) {
                var parts = rgba[1].split(/[,\s/]+/).filter(Boolean);
                if (parts.length >= 3) return { r: clamp01(parseFloat(parts[0]) / 255), g: clamp01(parseFloat(parts[1]) / 255), b: clamp01(parseFloat(parts[2]) / 255) };
            }
        }
        return null;
    }

    // ------------------------------------------------------ nearest CSS name --
    var NAMES = 'black:000000,white:ffffff,red:ff0000,lime:00ff00,blue:0000ff,yellow:ffff00,cyan:00ffff,magenta:ff00ff,silver:c0c0c0,gray:808080,maroon:800000,olive:808000,green:008000,purple:800080,teal:008080,navy:000080,orange:ffa500,pink:ffc0cb,brown:a52a2a,gold:ffd700,beige:f5f5dc,coral:ff7f50,crimson:dc143c,indigo:4b0082,ivory:fffff0,khaki:f0e68c,lavender:e6e6fa,salmon:fa8072,turquoise:40e0d0,violet:ee82ee,tan:d2b48c,tomato:ff6347,plum:dda0dd,orchid:da70d6,chocolate:d2691e,sienna:a0522d,peru:cd853f,wheat:f5deb3,skyblue:87ceeb,steelblue:4682b4,royalblue:4169e1,slateblue:6a5acd,seagreen:2e8b57,forestgreen:228b22,limegreen:32cd32,olivedrab:6b8e23,goldenrod:daa520,firebrick:b22222,darkred:8b0000,darkgreen:006400,darkblue:00008b,midnightblue:191970,teal:008080,aqua:00ffff,fuchsia:ff00ff,hotpink:ff69b4,deeppink:ff1493,tan:d2b48c,mintcream:f5fffa,mistyrose:ffe4e1,lightblue:add8e6,lightgreen:90ee90,lightyellow:ffffe0,lightpink:ffb6c1,lightgray:d3d3d3,darkgray:a9a9a9,dimgray:696969,slategray:708090';
    var NAME_TABLE = null;
    function buildNames() {
        if (NAME_TABLE) return NAME_TABLE;
        NAME_TABLE = [];
        NAMES.split(',').forEach(function (pair) {
            var kv = pair.split(':');
            var c = parseHex(kv[1]);
            if (c) NAME_TABLE.push({ name: kv[0], c: c });
        });
        return NAME_TABLE;
    }
    function nearestName(c) {
        var t = buildNames(), best = null, bd = Infinity;
        for (var i = 0; i < t.length; i++) {
            var d = deltaOK(c, t[i].c);
            if (d < bd) { bd = d; best = t[i]; }
        }
        return best ? { name: best.name, exact: bd < 1.2, delta: bd } : null;
    }
    // key → colour, for reading a colour NAME typed in the page's language
    function namedColor(key) {
        var t = buildNames();
        for (var i = 0; i < t.length; i++) if (t[i].name === key) return { r: t[i].c.r, g: t[i].c.g, b: t[i].c.b };
        return null;
    }

    // ---------------------------------------------------------------- mixing --
    // colors: [{r,g,b, weight, pig?}] (rgb in 0..1). Returns in-gamut {r,g,b} 0..1.
    function normWeights(colors) {
        var sum = 0; colors.forEach(function (c) { sum += (c.weight > 0 ? c.weight : 0); });
        if (sum <= 0) { return colors.map(function () { return 1 / colors.length; }); }
        return colors.map(function (c) { return (c.weight > 0 ? c.weight : 0) / sum; });
    }

    /* A prepared paint for a row: the measured pigment when the row is one,
       otherwise spectral.js's reconstruction of the row's sRGB (strength 1). */
    var _customCache = {};
    function paintOf(c) {
        var P = PAINT();
        if (!P) return null;
        if (c.pig) { var pg = P.pigment(c.pig); if (pg) return pg; }
        if (!SPECTRAL) return null;
        var key = hex(c);
        if (!_customCache[key]) {
            var v = to255(c);
            _customCache[key] = P.prepare(new SPECTRAL.Color([v[0], v[1], v[2]]).R, 1, { hex: key });
        }
        return _customCache[key];
    }
    function mixPaint(colors) {
        var P = PAINT();
        var anyPos = colors.some(function (c) { return c.weight > 0; });
        var use = colors.filter(function (c) { return !anyPos || c.weight > 0; });
        if (!use.length) return { r: 0, g: 0, b: 0 };
        if (P && (SPECTRAL || use.every(function (c) { return !!c.pig; }))) {
            var paints = use.map(paintOf);
            if (paints.every(Boolean)) {
                var rgb = P.mixRgb(paints, use.map(function (c) { return anyPos ? c.weight : 1; }));
                return { r: rgb[0] / 255, g: rgb[1] / 255, b: rgb[2] / 255 };
            }
        }
        if (!SPECTRAL) return mixPerceptual(colors); // graceful degrade
        // Volume semantics without the paint module: f = √volume.
        var args = use.map(function (c) {
            return [new SPECTRAL.Color(to255(c)), Math.sqrt(anyPos ? c.weight : 1)];
        });
        if (args.length === 1) { var s = args[0][0].sRGB; return { r: s[0] / 255, g: s[1] / 255, b: s[2] / 255 }; }
        var mixed = SPECTRAL.mix.apply(null, args);
        var g = mixed.toGamut ? mixed.toGamut() : mixed;
        var out = g.sRGB;
        return { r: clamp01(out[0] / 255), g: clamp01(out[1] / 255), b: clamp01(out[2] / 255) };
    }

    // Additive light: colours add like beams. Linear SUM scaled by weight, then
    // clamp. Equal blue + yellow -> white. (Weights act as relative intensity;
    // default 1 each, so the canonical demo "lights add to white" holds.)
    function mixLight(colors) {
        var r = 0, g = 0, b = 0;
        colors.forEach(function (c) {
            var w = c.weight > 0 ? c.weight : 0;
            r += s2l(clamp01(c.r)) * w; g += s2l(clamp01(c.g)) * w; b += s2l(clamp01(c.b)) * w;
        });
        return { r: clamp01(l2s(clamp01(r))), g: clamp01(l2s(clamp01(g))), b: clamp01(l2s(clamp01(b))) };
    }

    // Subtractive ink/filter: weighted geometric product in linear light.
    function mixInk(colors) {
        var p = normWeights(colors);
        var r = 1, g = 1, b = 1;
        colors.forEach(function (c, i) {
            r *= Math.pow(Math.max(1e-4, s2l(clamp01(c.r))), p[i]);
            g *= Math.pow(Math.max(1e-4, s2l(clamp01(c.g))), p[i]);
            b *= Math.pow(Math.max(1e-4, s2l(clamp01(c.b))), p[i]);
        });
        return { r: clamp01(l2s(clamp01(r))), g: clamp01(l2s(clamp01(g))), b: clamp01(l2s(clamp01(b))) };
    }

    // Perceptual OKLab weighted average — smooth blend.
    function mixPerceptual(colors) {
        var p = normWeights(colors), L = 0, a = 0, bb = 0;
        colors.forEach(function (c, i) {
            var o = rgbToOklab(clamp01(c.r), clamp01(c.g), clamp01(c.b));
            L += o.L * p[i]; a += o.a * p[i]; bb += o.b * p[i];
        });
        return gamutMapOklab(L, a, bb);
    }

    // Naive sRGB (gamma-space) weighted average — WRONG ON PURPOSE.
    function mixRgb(colors) {
        var p = normWeights(colors), r = 0, g = 0, b = 0;
        colors.forEach(function (c, i) { r += clamp01(c.r) * p[i]; g += clamp01(c.g) * p[i]; b += clamp01(c.b) * p[i]; });
        return { r: clamp01(r), g: clamp01(g), b: clamp01(b) };
    }

    var MODES = { paint: mixPaint, light: mixLight, ink: mixInk, perceptual: mixPerceptual, rgb: mixRgb };
    function mix(colors, mode) {
        var fn = MODES[mode] || mixPaint;
        return fn(colors);
    }

    // gradient strip of `steps` colours between the ordered stops, in `mode`.
    function gradient(colors, steps, mode) {
        if (colors.length < 2) return colors.slice();
        var out = [];
        for (var i = 0; i < steps; i++) {
            var t = steps === 1 ? 0 : i / (steps - 1);
            // position t across the multi-stop chain
            var seg = t * (colors.length - 1);
            var idx = Math.min(colors.length - 2, Math.floor(seg));
            var lt = seg - idx;
            var a = colors[idx], b = colors[idx + 1];
            var pair = [
                { r: a.r, g: a.g, b: a.b, pig: a.pig, weight: 1 - lt },
                { r: b.r, g: b.g, b: b.b, pig: b.pig, weight: lt }
            ];
            out.push(mix(pair, mode));
        }
        return out;
    }

    /* A v1 permalink / saved mix stored spectral.js FACTORS; paint weights are
       volumes now, and f² = volume, so squaring shows the colour it always did.
       Other models never squared anything and keep their weights. */
    function upgradeLegacyWeights(colors, mode) {
        if (mode !== 'paint') return colors;
        colors.forEach(function (c) { c.weight = c.weight * c.weight; });
        return colors;
    }

    var CMX = {
        clamp01: clamp01, round: round,
        s2l: s2l, l2s: l2s,
        rgbToHsl: rgbToHsl, hslToRgb: hslToRgb, rgbToCmyk: rgbToCmyk,
        rgbToOklab: rgbToOklab, oklabToRgb: oklabToRgb, oklabToOklch: oklabToOklch,
        gamutMapOklab: gamutMapOklab,
        relLum: relLum, contrastRatio: contrastRatio, deltaOK: deltaOK,
        simulateCVD: simulateCVD,
        to255: to255, hex: hex, rgbStr: rgbStr, hslStr: hslStr, cmykStr: cmykStr, oklchStr: oklchStr,
        parse: parse, parseHex: parseHex, nearestName: nearestName, namedColor: namedColor,
        mix: mix, gradient: gradient, upgradeLegacyWeights: upgradeLegacyWeights,
        MODES: Object.keys(MODES),
        hasSpectral: function () { return !!SPECTRAL; },
        _setSpectral: function (s) { SPECTRAL = s; }
    };

    if (typeof module !== 'undefined' && module.exports) module.exports = CMX;
    if (root) root.CMX = CMX;

    // ====================================================================== //
    //  DOM CONTROLLER (browser only). The Blade renders the static skeleton  //
    //  + translated labels; this wires behaviour and renders dynamic parts,  //
    //  reading runtime strings from window.__CMX_LANG.                       //
    // ====================================================================== //
    if (typeof document !== 'undefined') {
        if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initDOM);
        else initDOM();
    }

    function initDOM() {
        var rootEl = document.getElementById('cmx');
        if (!rootEl || rootEl.__cmxInit) return;
        rootEl.__cmxInit = true;
        if (root && root.spectral && !CMX.hasSpectral()) CMX._setSpectral(root.spectral);

        var L = (root && root.__CMX_LANG) || {};
        var PIG = (root && root.__CMX_PIGMENTS) || {};
        var t = function (k, d) { return L[k] != null ? L[k] : d; };
        var $ = function (id) { return document.getElementById(id); };
        var toast = function (m, ty) { try { (root.__toast || function () {})(m, ty || 'success'); } catch (e) {} };
        // Telemetry rides the allowed `feature_use` type discriminated by `f`
        // (custom top-level types are silently dropped by collect()). Dashboard
        // reads feature_use grouped by slug + f.
        var ae = function (f, data) {
            try { (root.__ae || function () {})('feature_use', Object.assign({ slug: 'color-mixer', f: f }, data || {})); } catch (e) {}
        };
        /* Funnel stages + a completion signal. A mix is "delivered" when the
           user takes the colour away with them: copies it, exports it, shares
           it, or gets a paint recipe back (color-mixer-recipe.js calls
           __cmxDone('recipe')). */
        var stage = function (s, d) { try { if (root.__stage) root.__stage(s, d || {}); } catch (e) {} };
        var _done = false;
        var done = function (via) {
            stage('complete', { via: via });
            if (_done) return; _done = true;
            try { if (root.__aeToolComplete) root.__aeToolComplete({ via: via }); } catch (e) {}
        };
        root.__cmxDone = done;
        var settings = root.__toolSettings;
        var P = PAINT();

        function el(tag, attrs, kids) {
            var n = document.createElement(tag);
            if (attrs) for (var k in attrs) {
                if (k === 'class') n.className = attrs[k];
                else if (k === 'html') n.innerHTML = attrs[k];
                else if (k === 'text') n.textContent = attrs[k];
                else if (k.slice(0, 2) === 'on' && typeof attrs[k] === 'function') n.addEventListener(k.slice(2), attrs[k]);
                else if (attrs[k] != null) n.setAttribute(k, attrs[k]);
            }
            if (kids) (Array.isArray(kids) ? kids : [kids]).forEach(function (c) { if (c != null) n.appendChild(typeof c === 'string' ? document.createTextNode(c) : c); });
            return n;
        }
        var copyTxt = function (s) {
            try { navigator.clipboard.writeText(s).then(function () { toast(t('copied', 'Copied')); }, function () {}); }
            catch (e) { }
        };
        function pigName(id) { return PIG[id] || id.replace(/_/g, ' '); }
        function pigRgb(id) {
            var pg = P && P.pigment(id);
            if (!pg) return null;
            var v = P.paintRgb(pg);
            return { r: v[0] / 255, g: v[1] / 255, b: v[2] / 255 };
        }

        // ---- tabs --------------------------------------------------------- //
        var tabMix = $('cmx-tab-mix'), tabRecipe = $('cmx-tab-recipe'), panelRecipe = $('cmr');
        var activeTab = 'mix';
        function showTab(which, opts) {
            opts = opts || {};
            if (which !== 'recipe') which = 'mix';
            if (which === activeTab && !opts.force) return;
            activeTab = which;
            root.__cmxTab = which;
            var isR = which === 'recipe';
            if (tabMix) { tabMix.setAttribute('aria-selected', isR ? 'false' : 'true'); tabMix.tabIndex = isR ? -1 : 0; }
            if (tabRecipe) { tabRecipe.setAttribute('aria-selected', isR ? 'true' : 'false'); tabRecipe.tabIndex = isR ? 0 : -1; }
            rootEl.hidden = isR;
            if (panelRecipe) panelRecipe.hidden = !isR;
            if (isR) { try { root.dispatchEvent(new CustomEvent('cmx:tab', { detail: { tab: 'recipe' } })); } catch (e) {} }
            else persist();
            if (!opts.silent) ae('tab', { t: which });
        }
        root.__cmxShowTab = showTab;
        [tabMix, tabRecipe].forEach(function (b) {
            if (!b) return;
            b.addEventListener('click', function () { showTab(b.getAttribute('data-tab')); });
            b.addEventListener('keydown', function (e) {
                if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
                e.preventDefault();
                var next = activeTab === 'mix' ? 'recipe' : 'mix';
                showTab(next);
                var nb = next === 'mix' ? tabMix : tabRecipe; if (nb) nb.focus();
            });
        });

        // ---- state -------------------------------------------------------- //
        var MODE_LIST = ['paint', 'light', 'ink', 'perceptual', 'rgb'];
        var DEFAULT = { mode: 'paint', colors: [{ r: 0, g: 0.23, b: 0.83, weight: 1 }, { r: 1, g: 0.83, b: 0, weight: 1 }] };
        var state = loadState();

        function loadState() {
            var h = parseHashState();
            if (h) return h;
            try {
                var saved = settings && settings.load('color-mixer', 'state', null);
                if (saved && saved.colors && saved.colors.length >= 2) {
                    if (saved.v !== 2) upgradeLegacyWeights(saved.colors, saved.mode);
                    saved.v = 2;
                    return saved;
                }
            } catch (e) {}
            return { v: 2, mode: DEFAULT.mode, colors: DEFAULT.colors.map(function (c) { return { r: c.r, g: c.g, b: c.b, weight: c.weight }; }) };
        }
        function hashParams() {
            var p = {};
            try {
                location.hash.replace(/^#/, '').split('&').forEach(function (kv) { if (!kv) return; var a = kv.split('='); p[a[0]] = decodeURIComponent(a[1] || ''); });
            } catch (e) {}
            return p;
        }
        function decodeColors(list) {
            return list.split(',').map(function (tok) {
                var seg = tok.split('-');
                var w = seg[1] ? Math.max(0, parseFloat(seg[1])) : 1;
                if (!(w >= 0) || isNaN(w)) w = 1;
                if (seg[0].indexOf('p.') === 0) {
                    var id = seg[0].slice(2), rgb = pigRgb(id);
                    if (!rgb) return null;
                    rgb.pig = id; rgb.weight = w; return rgb;
                }
                var c = CMX.parseHex(seg[0]); if (!c) return null;
                c.weight = w; return c;
            }).filter(Boolean);
        }
        function encodeColors(colors) {
            return colors.map(function (x) { return (x.pig ? 'p.' + x.pig : CMX.hex(x).slice(1)) + '-' + CMX.round(x.weight, 5); }).join(',');
        }
        function parseHashState() {
            try {
                var p = hashParams();
                if (!p.c) return null;
                var colors = decodeColors(p.c);
                if (colors.length < 2) return null;
                var mode = MODE_LIST.indexOf(p.m) >= 0 ? p.m : 'paint';
                if (p.v !== '2') upgradeLegacyWeights(colors, mode);
                return { v: 2, mode: mode, colors: colors };
            } catch (e) { return null; }
        }
        var saveTimer = null;
        function persist() {
            // URL hash for permalink — only while the mixer tab owns the page;
            // the recipe tab writes its own.
            if (activeTab === 'mix') {
                var newHash = '#m=' + state.mode + '&c=' + encodeColors(state.colors) + '&v=2';
                if (location.hash !== newHash) {
                    try { history.replaceState(null, '', newHash); } catch (e) { location.hash = newHash; }
                }
            }
            clearTimeout(saveTimer);
            saveTimer = setTimeout(function () { try { state.v = 2; settings && settings.save('color-mixer', 'state', state); } catch (e) {} }, 300);
        }

        // ---- render: mode selector --------------------------------------- //
        var modeBar = $('cmx-mode');
        function renderModes() {
            if (!modeBar) return;
            modeBar.innerHTML = '';
            MODE_LIST.forEach(function (m) {
                var btn = el('button', {
                    type: 'button', class: 'cmx-mode-btn' + (m === state.mode ? ' is-active' : ''),
                    'data-mode': m, 'aria-pressed': m === state.mode ? 'true' : 'false',
                    title: t('mode_' + m + '_desc', ''),
                    onclick: function () { state.mode = m; renderModes(); recompute(); ae('mix_mode', { mode: m }); }
                }, [
                    el('span', { class: 'cmx-mode-ico', text: t('mode_' + m + '_ico', '') }),
                    el('span', { class: 'cmx-mode-name', text: t('mode_' + m, m) })
                ]);
                modeBar.appendChild(btn);
            });
            var d = $('cmx-mode-desc');
            if (d) d.textContent = t('mode_' + state.mode + '_desc', '');
        }

        // ---- render: colour input rows ----------------------------------- //
        var hasEyedropper = typeof root.EyeDropper === 'function';
        // iOS Safari / some WebKit builds don't support <input type=color> — it
        // falls back to a text field, so an opacity:0 overlay would just focus an
        // invisible text box. Detect and, when unsupported, make the swatch focus
        // the visible hex field instead (and rely on the quick-colours palette).
        var COLOR_INPUT_OK = (function () { var i = document.createElement('input'); i.setAttribute('type', 'color'); return i.type === 'color'; })();
        var activeColorIndex = 0;
        var colorsWrap = $('cmx-colors');
        function renderColors() {
            if (!colorsWrap) return;
            colorsWrap.innerHTML = '';
            state.colors.forEach(function (c, i) {
                var hexv = CMX.hex(c);
                var picker = null, swatch;
                var hexIn = el('input', { type: 'text', value: hexv, class: 'cmx-hex', spellcheck: 'false', autocomplete: 'off', inputmode: 'text', 'aria-label': t('color_code', 'Colour code') + ' ' + (i + 1) });
                var pigLbl = el('span', { class: 'cmx-pig-lbl' });
                hexIn.addEventListener('focus', function () { activeColorIndex = i; });
                hexIn.addEventListener('input', function (e) {
                    var p = CMX.parse(e.target.value);
                    if (p) { c.r = p.r; c.g = p.g; c.b = p.b; delete c.pig; if (picker) picker.value = CMX.hex(c); swatch.style.background = CMX.hex(c); hexIn.classList.remove('is-err'); updatePig(i); recompute(); }
                    else { hexIn.classList.add('is-err'); }
                });

                if (COLOR_INPUT_OK) {
                    picker = el('input', { type: 'color', value: hexv, class: 'cmx-native', 'aria-label': t('pick_color', 'Pick colour') + ' ' + (i + 1) });
                    picker.addEventListener('input', function (e) { var p = CMX.parseHex(e.target.value); if (p) { c.r = p.r; c.g = p.g; c.b = p.b; delete c.pig; activeColorIndex = i; updateRow(i); recompute(); } });
                    swatch = el('label', { class: 'cmx-swatch', style: 'background:' + hexv });
                    swatch.appendChild(picker);
                } else {
                    swatch = el('button', { type: 'button', class: 'cmx-swatch', style: 'background:' + hexv, title: t('pick_color', 'Pick colour'), 'aria-label': t('pick_color', 'Pick colour') + ' ' + (i + 1) });
                    swatch.addEventListener('click', function () { activeColorIndex = i; hexIn.focus(); hexIn.select(); });
                }

                var eyeBtn = el('button', { type: 'button', class: 'cmx-icon-btn', title: t('eyedropper', 'Eyedropper'), 'aria-label': t('eyedropper', 'Eyedropper'), text: '🎯' });
                if (hasEyedropper) {
                    eyeBtn.addEventListener('click', function () {
                        try { new root.EyeDropper().open().then(function (r) { var p = CMX.parseHex(r.sRGBHex); if (p) { c.r = p.r; c.g = p.g; c.b = p.b; delete c.pig; updateRow(i); recompute(); } }, function () {}); } catch (e) {}
                    });
                } else { eyeBtn.disabled = true; eyeBtn.title = t('eyedropper_unsupported', 'Eyedropper not supported in this browser'); }

                var wRange = el('input', { type: 'range', min: '0', max: '10', step: '0.1', value: Math.min(10, c.weight), class: 'cmx-weight-range', 'aria-label': t('weight', 'Amount') + ' ' + (i + 1) });
                var wNum = el('input', { type: 'number', min: '0', max: '100', step: '0.1', value: CMX.round(c.weight, 2), class: 'cmx-weight-num', 'aria-label': t('weight', 'Amount') + ' ' + (i + 1) });
                wRange.addEventListener('input', function (e) { c.weight = parseFloat(e.target.value) || 0; wNum.value = CMX.round(c.weight, 2); recompute(true); });
                wNum.addEventListener('input', function (e) { c.weight = Math.max(0, parseFloat(e.target.value) || 0); wRange.value = Math.min(10, c.weight); recompute(true); });

                var rm = el('button', { type: 'button', class: 'cmx-icon-btn cmx-rm', title: t('remove', 'Remove'), 'aria-label': t('remove', 'Remove') + ' ' + (i + 1), text: '✕' });
                rm.addEventListener('click', function () { if (state.colors.length > 2) { state.colors.splice(i, 1); renderColors(); recompute(); } });
                if (state.colors.length <= 2) rm.disabled = true;

                var row = el('div', { class: 'cmx-row' }, [
                    swatch,
                    el('div', { class: 'cmx-row-main' }, [
                        el('div', { class: 'cmx-row-top' }, [hexIn, eyeBtn, rm]),
                        pigLbl,
                        el('div', { class: 'cmx-row-weight' }, [wRange, wNum, el('span', { class: 'cmx-pct', id: 'cmx-pct-' + i })])
                    ])
                ]);
                row.__refs = { swatch: swatch, hexIn: hexIn, picker: picker, pigLbl: pigLbl };
                colorsWrap.appendChild(row);
                updatePig(i);
            });
            updatePercents();
            var addBtn = $('cmx-add');
            if (addBtn) addBtn.disabled = state.colors.length >= 6;
        }
        function updatePig(i) {
            var row = colorsWrap && colorsWrap.children[i]; if (!row || !row.__refs) return;
            var c = state.colors[i];
            row.__refs.pigLbl.textContent = c.pig ? pigName(c.pig) : '';
            row.__refs.pigLbl.hidden = !c.pig;
        }
        function updateRow(i) {
            var row = colorsWrap.children[i]; if (!row || !row.__refs) return;
            var c = state.colors[i]; var hx = CMX.hex(c);
            row.__refs.swatch.style.background = hx;
            row.__refs.hexIn.value = hx; row.__refs.hexIn.classList.remove('is-err');
            if (row.__refs.picker) row.__refs.picker.value = hx;
            updatePig(i);
        }
        function updatePercents() {
            var sum = 0; state.colors.forEach(function (c) { sum += c.weight > 0 ? c.weight : 0; });
            state.colors.forEach(function (c, i) {
                var p = $('cmx-pct-' + i); if (p) p.textContent = sum > 0 ? Math.round((c.weight > 0 ? c.weight : 0) / sum * 100) + '%' : '–';
            });
        }

        // ---- render: result + formats + a11y ----------------------------- //
        var FMT = [
            { k: 'hex', label: t('fmt_hex', 'HEX'), fn: CMX.hex },
            { k: 'rgb', label: t('fmt_rgb', 'RGB'), fn: CMX.rgbStr },
            { k: 'hsl', label: t('fmt_hsl', 'HSL'), fn: CMX.hslStr },
            { k: 'cmyk', label: t('fmt_cmyk', 'CMYK'), fn: CMX.cmykStr },
            { k: 'oklch', label: t('fmt_oklch', 'OKLCH'), fn: CMX.oklchStr }
        ];
        var CVD_LIST = [
            { k: 'protanopia', label: t('cvd_protanopia', 'Protanopia') },
            { k: 'deuteranopia', label: t('cvd_deuteranopia', 'Deuteranopia') },
            { k: 'tritanopia', label: t('cvd_tritanopia', 'Tritanopia') }
        ];
        var lastResult = null;
        var booted = false, sentFirstMix = false;
        function recompute() {
            var res = CMX.mix(state.colors, state.mode);
            lastResult = res;
            var hx = CMX.hex(res);
            // result swatch
            var rs = $('cmx-result-swatch'); if (rs) rs.style.background = hx;
            var rh = $('cmx-result-hex'); if (rh) rh.textContent = hx;
            // name
            var nm = CMX.nearestName(res);
            var rn = $('cmx-result-name');
            // only names this page can say in its own language — «≈ Darkgray» on a Russian page reads as a bug
            if (rn) rn.textContent = nm && L['name_' + nm.name] ? (nm.exact ? '' : '≈ ') + L['name_' + nm.name] : '';
            // formats
            var fl = $('cmx-formats');
            if (fl) {
                fl.innerHTML = '';
                FMT.forEach(function (f) {
                    var val = f.fn(res);
                    var row = el('div', { class: 'cmx-fmt' }, [
                        el('span', { class: 'cmx-fmt-label', text: f.label }),
                        el('code', { class: 'cmx-fmt-val', text: val }),
                        el('button', { type: 'button', class: 'cmx-copy', title: t('copy', 'Copy'), 'aria-label': t('copy', 'Copy') + ' ' + f.label, text: '⧉', onclick: function () { copyTxt(val); ae('copy_format', { fmt: f.k }); } })
                    ]);
                    fl.appendChild(row);
                });
            }
            // a11y: contrast
            var cw = CMX.contrastRatio(res, { r: 1, g: 1, b: 1 });
            var cb = CMX.contrastRatio(res, { r: 0, g: 0, b: 0 });
            var ac = $('cmx-contrast');
            if (ac) {
                ac.innerHTML = '';
                [['#fff', cw, t('on_white', 'On white')], ['#000', cb, t('on_black', 'On black')]].forEach(function (p) {
                    var ratio = p[1];
                    var pill = function (ok, lbl) { return el('span', { class: 'cmx-pill ' + (ok ? 'ok' : 'no'), text: lbl + (ok ? ' ✓' : ' ✗') }); };
                    ac.appendChild(el('div', { class: 'cmx-contrast-card', style: 'background:' + p[0] }, [
                        el('div', { class: 'cmx-contrast-sample', style: 'color:' + hx, text: 'Aa' }),
                        el('div', { class: 'cmx-contrast-meta' }, [el('b', { text: CMX.round(ratio, 2) + ':1' }), pill(ratio >= 4.5, 'AA'), pill(ratio >= 7, 'AAA')]),
                        el('div', { class: 'cmx-contrast-lbl', text: p[2] })
                    ]));
                });
            }
            // a11y: CVD
            var cvd = $('cmx-cvd');
            if (cvd) {
                cvd.innerHTML = '';
                cvd.appendChild(el('div', { class: 'cmx-cvd-cell' }, [el('div', { class: 'cmx-cvd-sw', style: 'background:' + hx }), el('span', { class: 'cmx-cvd-lbl', text: t('cvd_normal', 'Normal') })]));
                CVD_LIST.forEach(function (v) {
                    cvd.appendChild(el('div', { class: 'cmx-cvd-cell' }, [el('div', { class: 'cmx-cvd-sw', style: 'background:' + CMX.hex(CMX.simulateCVD(res, v.k)) }), el('span', { class: 'cmx-cvd-lbl', text: v.label })]));
                });
            }
            renderCompare();
            renderGradient();
            updatePercents();
            persist();
            scheduleRecent();
            // Engagement signal: once per session, after the user's first real
            // change (not the boot render). recompute() per slider-drag would
            // flood, so we emit a single "mix" feature_use.
            if (booted && !sentFirstMix) { sentFirstMix = true; ae('mix', { mode: state.mode, n: state.colors.length, pig: state.colors.filter(function (c) { return c.pig; }).length }); }
        }

        // ---- comparison: same colours across all 5 models ---------------- //
        function renderCompare() {
            var box = $('cmx-compare'); if (!box) return;
            box.innerHTML = '';
            MODE_LIST.forEach(function (m) {
                var res = CMX.mix(state.colors, m);
                var hx = CMX.hex(res);
                var card = el('button', {
                    type: 'button', class: 'cmx-cmp-card' + (m === state.mode ? ' is-active' : ''),
                    onclick: function () { state.mode = m; renderModes(); recompute(); }
                }, [
                    el('div', { class: 'cmx-cmp-sw', style: 'background:' + hx }),
                    el('div', { class: 'cmx-cmp-name', text: t('mode_' + m + '_ico', '') + ' ' + t('mode_' + m, m) }),
                    el('code', { class: 'cmx-cmp-hex', text: hx })
                ]);
                box.appendChild(card);
            });
        }

        // ---- gradient strip ---------------------------------------------- //
        var gSteps = 9;
        function renderGradient() {
            var strip = $('cmx-gradient'); if (!strip) return;
            strip.innerHTML = '';
            var g = CMX.gradient(state.colors, gSteps, state.mode);
            g.forEach(function (c) {
                var hx = CMX.hex(c);
                strip.appendChild(el('button', { type: 'button', class: 'cmx-grad-cell', style: 'background:' + hx, title: hx, 'aria-label': hx, onclick: function () { copyTxt(hx); } }));
            });
        }

        // ---- wiring: add / share / export / gradient steps --------------- //
        var addBtn = $('cmx-add');
        if (addBtn) addBtn.addEventListener('click', function () {
            if (state.colors.length >= 6) return;
            state.colors.push({ r: Math.random() * 0.6 + 0.2, g: Math.random() * 0.6 + 0.2, b: Math.random() * 0.6 + 0.2, weight: 1 });
            renderColors(); recompute(); ae('add_color', { n: state.colors.length });
        });
        var stepsRange = $('cmx-gradient-steps');
        if (stepsRange) {
            stepsRange.value = gSteps;
            var sv = $('cmx-gradient-steps-val'); if (sv) sv.textContent = gSteps;
            stepsRange.addEventListener('input', function (e) { gSteps = parseInt(e.target.value, 10) || 9; if (sv) sv.textContent = gSteps; renderGradient(); });
        }
        var shareBtn = $('cmx-share');
        if (shareBtn) shareBtn.addEventListener('click', function () {
            persist();
            var url = location.href;
            if (navigator.share) { navigator.share({ title: document.title, url: url }).catch(function () {}); }
            else copyTxt(url);
            toast(t('link_copied', 'Link copied'));
            ae('share', {}); done('share');
        });
        var pngBtn = $('cmx-export-png');
        if (pngBtn) pngBtn.addEventListener('click', function () {
            if (!lastResult) return;
            var W = 600, H = 360, cv = document.createElement('canvas'); cv.width = W; cv.height = H;
            var x = cv.getContext('2d'); var hx = CMX.hex(lastResult);
            x.fillStyle = hx; x.fillRect(0, 0, W, H - 70);
            x.fillStyle = '#111'; x.fillRect(0, H - 70, W, 70);
            x.fillStyle = '#fff'; x.font = '600 30px ui-monospace,Menlo,monospace'; x.textBaseline = 'middle';
            x.fillText(hx.toUpperCase(), 24, H - 35);
            x.font = '15px system-ui,sans-serif'; x.fillStyle = '#9aa'; x.textAlign = 'right';
            x.fillText('timbrica', W - 24, H - 35);
            cv.toBlob(function (b) { if (b && root.__downloadFile) root.__downloadFile(b, 'color-mix-' + hx.slice(1) + '.png'); else if (b) { var a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = 'color-mix.png'; document.body.appendChild(a); a.click(); } });
            ae('export_png', {}); done('png');
        });
        var cssBtn = $('cmx-export-css');
        if (cssBtn) cssBtn.addEventListener('click', function () {
            if (!lastResult) return;
            var hx = CMX.hex(lastResult);
            copyTxt(':root{\n  --mixed-color: ' + hx + ';\n  --mixed-rgb: ' + CMX.rgbStr(lastResult) + ';\n  --mixed-oklch: ' + CMX.oklchStr(lastResult) + ';\n}');
            ae('export_css', {}); done('css');
        });
        $('cmx-result-hex') && $('cmx-result-hex').addEventListener('click', function () { if (lastResult) { copyTxt(CMX.hex(lastResult)); done('copy_hex'); } });

        // ---- bridge to the recipe tab ------------------------------------ //
        var toRecipe = $('cmx-to-recipe');
        if (toRecipe) toRecipe.addEventListener('click', function () {
            if (!lastResult) return;
            ae('to_recipe', {});
            showTab('recipe');
            if (typeof root.__cmrSetTarget === 'function') root.__cmrSetTarget(to255(lastResult), 'mixer');
        });
        /* The recipe tab hands a recipe back: rows = [{pig|hex, weight}] */
        root.__cmxLoadMix = function (rows) {
            var cols = rows.map(function (r) {
                if (r.pig) { var c = pigRgb(r.pig); if (!c) return null; c.pig = r.pig; c.weight = r.weight; return c; }
                var h = CMX.parseHex(r.hex); if (!h) return null; h.weight = r.weight; return h;
            }).filter(Boolean);
            if (!cols.length) return;
            while (cols.length < 2) cols.push({ r: cols[0].r, g: cols[0].g, b: cols[0].b, pig: cols[0].pig, weight: 0 });
            state.mode = 'paint';
            state.colors = cols.slice(0, 6);
            showTab('mix');
            renderModes(); renderColors(); recompute();
            var top = $('cmx'); if (top) top.scrollIntoView({ block: 'nearest' });
        };

        // ---- preset color-pair chips (FAQ swatches load into mixer) ------- //
        Array.prototype.forEach.call(document.querySelectorAll('[data-cmx-load]'), function (chip) {
            chip.addEventListener('click', function () {
                var spec = chip.getAttribute('data-cmx-load'); // "ff0000,00ff00" optional "|mode"
                var parts = spec.split('|'); var cols = parts[0].split(',').map(function (h) { var c = CMX.parseHex(h.trim()); if (c) c.weight = 1; return c; }).filter(Boolean);
                if (cols.length >= 2) {
                    state.colors = cols; if (parts[1] && MODE_LIST.indexOf(parts[1]) >= 0) state.mode = parts[1];
                    showTab('mix');
                    renderModes(); renderColors(); recompute();
                    var top = $('cmx'); if (top) top.scrollIntoView({ block: 'nearest' });
                    ae('preset_pair', {});
                }
            });
        });

        // ---- randomize --------------------------------------------------- //
        var randBtn = $('cmx-randomize');
        if (randBtn) {
            randBtn.title = t('randomize', 'Randomize');
            randBtn.setAttribute('aria-label', t('randomize', 'Randomize'));
            randBtn.addEventListener('click', function () {
                state.colors.forEach(function (c) {
                    var rnd = randInt(0, 0xffffff).toString(16).padStart(6, '0');
                    var p = CMX.parseHex(rnd); c.r = p.r; c.g = p.g; c.b = p.b; delete c.pig;
                });
                renderColors(); recompute(); ae('randomize', {});
            });
        }
        function randInt(lo, hi) { return lo + Math.floor((typeof crypto !== 'undefined' && crypto.getRandomValues ? crypto.getRandomValues(new Uint32Array(1))[0] / 4294967296 : Math.random()) * (hi - lo + 1)); }

        // ---- quick colours + real pigments (active row) ------------------ //
        var QUICK = ['#ffffff', '#000000', '#808080', '#e2231a', '#ff8c00', '#ffd400',
            '#2e8b57', '#1a3cae', '#6a0dad', '#ffc0cb', '#8a3324', '#00a0a0'];
        function renderQuick() {
            var lbl = $('cmx-quick-lbl'); if (lbl) lbl.textContent = t('quick_colors', 'Quick colors');
            var strip = $('cmx-quick');
            if (strip) {
                strip.innerHTML = '';
                QUICK.forEach(function (hx) {
                    var c0 = CMX.parseHex(hx); if (!c0) return;
                    strip.appendChild(el('button', {
                        type: 'button', class: 'cmx-quick-sw', style: 'background:' + hx, title: hx, 'aria-label': hx,
                        onclick: function () {
                            var idx = Math.min(activeColorIndex, state.colors.length - 1);
                            var c = state.colors[idx]; c.r = c0.r; c.g = c0.g; c.b = c0.b; delete c.pig;
                            updateRow(idx); recompute();
                        }
                    }));
                });
            }
            var ps = $('cmx-quick-pig');
            if (ps && P) {
                ps.innerHTML = '';
                var white = P.pigment('titanium_white');
                P.libraryIds().forEach(function (id) {
                    var pg = P.pigment(id), mass = P.hexOfRgb(P.paintRgb(pg)), tint = P.hexOfRgb(P.tintRgb(pg, white, 0.2));
                    ps.appendChild(el('button', {
                        type: 'button', class: 'cmx-quick-sw', title: pigName(id), 'aria-label': pigName(id),
                        style: 'background:linear-gradient(135deg,' + mass + ' 50%,' + tint + ' 50%)',
                        onclick: function () {
                            var idx = Math.min(activeColorIndex, state.colors.length - 1);
                            var c = state.colors[idx], rgb = pigRgb(id);
                            c.r = rgb.r; c.g = rgb.g; c.b = rgb.b; c.pig = id;
                            if (state.mode !== 'paint') { state.mode = 'paint'; renderModes(); }
                            updateRow(idx); recompute(); ae('quick_pigment', { id: id });
                        }
                    }));
                });
            }
        }

        // ---- recent mixes (localStorage) -------------------------------- //
        var RECENT_KEY = 'cmx-recent', recentTimer = null;
        function loadRecent() { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); } catch (e) { return []; } }
        function saveRecent(a) { try { localStorage.setItem(RECENT_KEY, JSON.stringify(a.slice(0, 12))); } catch (e) {} }
        // v2| prefix = paint weights are volumes; entries without it are v1 factors.
        function curEncoding() { return 'v2|' + state.mode + '|' + encodeColors(state.colors); }
        function scheduleRecent() {
            if (!booted) return;
            clearTimeout(recentTimer);
            recentTimer = setTimeout(function () {
                if (!lastResult) return;
                var enc = curEncoding(), hx = CMX.hex(lastResult);
                var arr = loadRecent().filter(function (x) { return x.e !== enc; });
                arr.unshift({ e: enc, hex: hx });
                saveRecent(arr); renderRecent();
            }, 900);
        }
        function applyEncoding(enc) {
            var parts = enc.split('|'), v2 = parts[0] === 'v2';
            if (v2) parts.shift();
            if (parts.length < 2) return;
            var cols = decodeColors(parts[1]);
            if (cols.length < 2) return;
            var mode = MODE_LIST.indexOf(parts[0]) >= 0 ? parts[0] : 'paint';
            if (!v2) upgradeLegacyWeights(cols, mode);
            state.colors = cols; state.mode = mode;
            renderModes(); renderColors(); recompute();
        }
        function renderRecent() {
            var block = $('cmx-recent-block'), strip = $('cmx-recent'), title = $('cmx-recent-title');
            if (!strip) return;
            if (title) title.textContent = t('recent_mixes', 'Recent mixes');
            var arr = loadRecent();
            if (block) block.hidden = arr.length === 0;
            strip.innerHTML = '';
            arr.forEach(function (item) {
                strip.appendChild(el('button', { type: 'button', class: 'cmx-recent-cell', style: 'background:' + item.hex, title: item.hex, 'aria-label': item.hex, onclick: function () { applyEncoding(item.e); } }));
            });
        }

        window.addEventListener('hashchange', function () {
            var hp = hashParams();
            if (hp.t === 'recipe') {
                /* read the new target first: showTab() makes the recipe tab
                   rewrite the hash from its current state */
                if (typeof root.__cmrFromHash === 'function') root.__cmrFromHash();
                showTab('recipe');
                return;
            }
            var h = parseHashState();
            if (h) { state = h; showTab('mix', { silent: true }); renderModes(); renderColors(); recompute(); }
        });

        // ---- boot -------------------------------------------------------- //
        if (!CMX.hasSpectral()) { var w = $('cmx-spectral-warn'); if (w) w.hidden = false; }
        renderModes();
        renderColors();
        renderQuick();
        renderRecent();
        var bootTab = hashParams().t === 'recipe' ? 'recipe' : 'mix';
        activeTab = bootTab === 'recipe' ? 'mix' : 'recipe'; // force the first showTab to apply
        showTab(bootTab, { silent: true, force: true });
        recompute();
        booted = true;
        stage('ready');
    }

})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : this));
